import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Field,
  Button,
  Column,
  Table,
  Spacer,
  Select,
  Flex,
  Toggle,
  Input,
} from '@oliasoft-open-source/react-ui-library';
import { toNum } from '@oliasoft-open-source/units';
import { validateNumber } from 'src/validation/common/validate-number';
import { updateCurrencyTemplate, getCurrencies } from 'store/entities/company-settings/company-settings';
import translations from 'src/internationalisation/translation-map.json';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';

const CurrencyTemplateForm = ({
  currencyTemplate,
  isAdding,
  currencyOptions,
  getCurrencies,
  updateCurrencyTemplate,
}) => {
  const { t } = useTranslation();
  const [primaryCurrency, setPrimaryCurrency] = useState(null);
  const [additionalCurrenciesList, setAdditionalCurrenciesList] = useState([]);
  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  useEffect(() => {
    getCurrencies();
  }, []);

  const mappedCurrencyOptions = currencyOptions.map((c) => ({
    ...c,
    disabled: additionalCurrenciesList.some((cur) => cur.currency === c.value || primaryCurrency === c.value),
  }));

  const currentCurrencyTemplate = React.useMemo(
    () => currencyTemplate?.find((template) => template.active),
    [currencyTemplate],
  );
  const primaryCurrencyStore = React.useMemo(
    () => currentCurrencyTemplate?.primaryCurrency || null,
    [currentCurrencyTemplate],
  );
  const additionalCurrenciesStore = React.useMemo(
    () => currentCurrencyTemplate?.additionalCurrencies || [],
    [currentCurrencyTemplate],
  );
  const nameStore = React.useMemo(() => currentCurrencyTemplate?.name || '', [currentCurrencyTemplate]);
  const currencyTemplateId = React.useMemo(
    () => currentCurrencyTemplate?.currencyTemplateId || null,
    [currentCurrencyTemplate],
  );
  const isDefaultStore = React.useMemo(() => currentCurrencyTemplate?.isDefault || false, [currentCurrencyTemplate]);

  const isCurrenciesListChanged =
    JSON.stringify(additionalCurrenciesStore) !== JSON.stringify(additionalCurrenciesList) ||
    primaryCurrencyStore !== primaryCurrency;
  const isNameChanged = nameStore !== name;
  const isDefaultChanged = isDefaultStore !== isDefault;

  const isFormChanged = isCurrenciesListChanged || isNameChanged || isDefaultChanged;
  const hasErros = additionalCurrenciesList.some((cur) => validateNumber(cur.exchangeRate));

  useEffect(() => {
    setPrimaryCurrency(primaryCurrencyStore);
  }, [primaryCurrencyStore]);

  useEffect(() => {
    setAdditionalCurrenciesList(additionalCurrenciesStore);
  }, [additionalCurrenciesStore]);

  useEffect(() => {
    setName(nameStore);
  }, [nameStore]);

  useEffect(() => {
    setIsDefault(isDefaultStore);
  }, [nameStore]);

  const onAddCurrency = () =>
    setAdditionalCurrenciesList([
      ...additionalCurrenciesList,
      {
        currency: mappedCurrencyOptions.filter((o) => !o.disabled)[0]?.value,
        exchangeRate: 100,
      },
    ]);

  const onChangeAdditionalCurrency = (ev, i) => {
    const newElement = [...additionalCurrenciesList];
    if (ev.target.name === 'currency') {
      newElement[i] = {
        ...newElement[i],
        currency: ev.target.value,
      };
    } else if (ev.target.name === 'exchangeRate') {
      newElement[i] = {
        ...newElement[i],
        exchangeRate: toNum(ev.target.value),
      };
    }
    setAdditionalCurrenciesList(newElement);
  };

  const onRemoveCurrency = (i) => {
    const newCurrencies = [...additionalCurrenciesList];
    newCurrencies.splice(i, 1);
    setAdditionalCurrenciesList(newCurrencies);
  };

  const onSubmit = () => {
    updateCurrencyTemplate({
      primaryCurrency,
      additionalCurrencies: additionalCurrenciesList,
      name,
      isDefault,
      currencyTemplateId,
      companyId: currentCurrencyTemplate.companyId,
    });
  };

  return (
    <Column scroll padding spacing={0}>
      <>
        <Flex>
          <Column spacing={0}>
            <Field label={t(translations.settings_templateName)}>
              <Input name="name" width="258px" onChange={(e) => setName(e.target.value)} value={name} />
            </Field>
          </Column>
          <Spacer />
          <Column spacing={0}>
            <Field label={t(translations.default_currency_template)}>
              <Toggle
                name="isDefault"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                disabled={isDefault}
              />
            </Field>
          </Column>
        </Flex>
        <Field label={t(translations.projectSettings_primaryCurrency)}>
          <Select
            name="primaryCurrency"
            options={mappedCurrencyOptions}
            value={primaryCurrency}
            onChange={(e) => {
              setPrimaryCurrency(e.target.value);
            }}
            disabled
          />
        </Field>
        <Table
          table={{
            fixedWidth: '100%',
            columnAlignment: ['left', 'right', 'left'],
            headers: [
              {
                cells: [
                  {
                    value: t(translations.projectSettings_additionalCurrency),
                  },
                  {
                    value: t(translations.projectSettings_exchangeRate),
                    style: { width: '120px' },
                  },
                  { value: '', style: { width: '90px' } },
                ],
                actions: [
                  {
                    primary: true,
                    label: t(translations.add),
                    icon: 'add',
                    disabled: additionalCurrenciesList.length >= 10,
                    onClick: () => {
                      onAddCurrency();
                    },
                  },
                ],
              },
            ],
            rows: additionalCurrenciesList.map((element, i) => ({
              cells: [
                {
                  name: 'currency',
                  type: 'Select',
                  options: mappedCurrencyOptions,
                  value: element.currency,
                  onChange: (ev) => {
                    onChangeAdditionalCurrency(ev, i);
                  },
                  autoLayerWidth: true,
                },
                {
                  name: 'exchangeRate',
                  type: 'Input',
                  error: validateNumber(element.exchangeRate),
                  value: element.exchangeRate,
                  onChange: (ev) => {
                    onChangeAdditionalCurrency(ev, i);
                  },
                },
                {
                  value: `= 1 ${primaryCurrency}`,
                  disabled: true,
                  style: {
                    opacity: '0.5',
                    cursor: 'default',
                  },
                },
              ],
              actions: [
                {
                  label: 'Delete',
                  icon: 'minus',
                  onClick: () => {
                    onRemoveCurrency(i);
                  },
                },
              ],
            })),
          }}
        />
        <Spacer />
        <Button
          label={t(translations.save)}
          onClick={onSubmit}
          disabled={isAdding || !isFormChanged || hasErros}
          colored
        />
      </>
    </Column>
  );
};

const mapStateToProps = ({ entities }) => {
  const { isAdding, currenciesList, currencyTemplate } = entities.companySettings;

  const currencyOptions = currenciesList.map((item) => ({
    label: `(${item.code}) ${item.name}`,
    value: item.code,
  }));

  return { isAdding, currencyTemplate, currencyOptions };
};

const mapDispatchToProps = { updateCurrencyTemplate, getCurrencies };

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(CurrencyTemplateForm));

export { Container as CurrencyTemplateForm };
